import { TranslateService } from '@ngx-translate/core';
import { CSDataWithSignals, deviceKeys, DSSignalItemData, MetmastsDataWithSignals, SignalList, TurbineDataWithSignals, TurbineSignalGroups } from 'app/shared/models/data-studio.model';

function getDefaultSignal({ name, unit }) {
  return {
    checked: false,
    name: 'SIGNALS.' + name + '.DISPLAY_NAME',
    value: name,
    unit,
    description: 'SIGNALS.' + name + '.DESCRIPTION'
  };
}

function addSignalToCollection(signals: any[], signal: any, properties: any, includes?: string[]): boolean {
  if (
    signals &&
    !signals.find(signal2 => signal2.value === signal.name) &&
    (!includes || includes.find(key => signal.name.includes(key))) &&
    (!properties.objectRef || properties.objectRef[signal.name])
  ) {
    signals.push({ ...getDefaultSignal(signal), ...properties });
    return true;
  }
  return false;
}

export function addSignal(signals: any[], signal: any, includes?: string[]): boolean {
  return addSignalToCollection(signals, signal, {}, includes);
}

export function addSignalWithObjectRef(signals: any[], signal: any, ref: any, includes?: string[]): boolean {
  return addSignalToCollection(signals, signal, { objectRef: ref }, includes);
}

export function addSignalWithTurbineType(signals: any[], signal: any, turbine: any, includes?: string[]): boolean {
  return addSignalToCollection(signals, signal, { turbineType: [turbine.type] }, includes);
}

export function addSignalWithTurbineTypeAndObjectRef(signals: any[], signal: any, turbine: any, ref: any, includes?: string[]): boolean {
  return addSignalToCollection(signals, signal, { turbineType: [turbine.type], objectRef: ref }, includes);
}

export function createTurbineSignalDevicesMapper(devices: TurbineDataWithSignals[]): Map<string, { name: string, id: string }[]> {
  const map = new Map<string, { name: string, id: string }[]>();
  devices.forEach(device => {
    Object.values(TurbineSignalGroups).forEach(groupName => {
      device[groupName].forEach(signal => {
        const deviceInfo = {
          id: device._id,
          name: device.code.replace(/_/g, ' ')
        };

        const existing = map.get(signal.name);
        map.set(signal.name, existing ? [...existing, deviceInfo] : [deviceInfo]);
      });
    });
  });

  return map;
}

export function createTurbineDeviceSignalsMapper(devices: TurbineDataWithSignals[]): Map<string, SignalList[]> {
  const map = new Map<string, SignalList[]>();
  devices.forEach(device =>
    map.set(device._id, Object.values(TurbineSignalGroups).flatMap(group => device[group]
      .map(({ name, displayName, unit }) => ({ name, displayName, unit, selected: false }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName))))
  )

  return map;
}

export function createCSSignalDevicesMapper(devices: CSDataWithSignals[]): Map<string, { name: string, id: string }[]> {
  const map = new Map<string, { name: string, id: string }[]>();
  devices.forEach(device => {
    device.signalsList.forEach(signal => {
      const deviceInfo = {
        id: device._id,
        name: device.code.replace(/_/g, ' ')
      };

      const existing = map.get(signal.dbName);
      map.set(signal.dbName, existing ? [...existing, deviceInfo] : [deviceInfo]);
    });
  });

  return map;
}

export function createCSDeviceSignalsMapper(devices: CSDataWithSignals[]): Map<string, SignalList[]> {
  const map = new Map<string, SignalList[]>();
  devices.forEach(device =>
    map.set(device._id, device.signalsList.flatMap(({ dbName, displayName, unit }) =>
      ({ name: dbName, displayName, unit, selected: false })).sort((a, b) => a.displayName.localeCompare(b.displayName))))

  return map;
}

export function createMetmastsSignalDevicesMapper(devices: MetmastsDataWithSignals[]): Map<string, { name: string, id: string }[]> {
  const map = new Map<string, { name: string, id: string }[]>();
  devices.forEach(device => {
    device.sensorsList.forEach(sensor => sensor.signalsList.forEach(signal => {
      const deviceInfo = {
        id: sensor._id,
        name: sensor.name
      };

      const existing = map.get(signal.dbName);
      map.set(signal.dbName, existing ? [...existing, deviceInfo] : [deviceInfo]);
    }));
  });

  return map;
}

export function createMetmastsDeviceSignalsMapper(devices: MetmastsDataWithSignals[]): Map<string, SignalList[]> {
  const map = new Map<string, SignalList[]>();
  const sensors = devices.flatMap(device => device.sensorsList);
  sensors.forEach(sensor =>
    map.set(sensor._id, sensor.signalsList.map(({ dbName, displayName, unit }) =>
      ({ name: dbName, displayName, unit, selected: false })).sort((a, b) => a.displayName.localeCompare(b.displayName))));

  return map;
}

export function createTurbineSignalTypesMaper(devices: TurbineDataWithSignals[]): Map<string, string[]> {
  const map = new Map<string, Set<string>>();

  devices.forEach(device => {
    Object.values(TurbineSignalGroups).forEach(groupName => {
      const signals = device[groupName];

      signals.forEach(signal => {
        if (!map.has(signal.name)) {
          map.set(signal.name, new Set<string>());
        }
        map.get(signal.name)!.add(device.type);
      });
    });
  });

  const result = new Map<string, string[]>();
  map.forEach((types, signalName) => {
    result.set(signalName, Array.from(types));
  });

  return result;
}

export function checkIncludes(a: string[], b: string[]): boolean {
  const set = new Set(a);
  return b.every(item => set.has(item));
}

export function getMeterSignals(currency: string) {
  return {
    productionScada: {
      unit: 'MWh',
      name: 'Production Scada',
      translateId: 'SIGNALS.PRODUCTION_SCADA.DISPLAY_NAME'
    },
    productionGrid: {
      unit: 'MWh',
      name: 'Production Grid',
      translateId: 'SIGNALS.PRODUCTION_GRID.DISPLAY_NAME'
    },
    electricityLosses: {
      unit: '%',
      name: 'Electricity Losses',
      translateId: 'SIGNALS.ELECTRICITY.DISPLAY_NAME'
    },
    consumptionGrid: {
      unit: 'MWh',
      name: 'Energy Consumption',
      translateId: 'SIGNALS.ENERGY_CONSUMPTION.DISPLAY_NAME'
    },
    electricitySalesRevenue: {
      unit: currency,
      name: 'Revenues Electricity Sales',
      translateId: 'SIGNALS.REV_ELEC.DISPLAY_NAME'
    },
    incentiveRevenue: {
      unit: currency,
      name: 'Revenues Incentive',
      translateId: 'SIGNALS.REV_INC.DISPLAY_NAME'
    },
    totalRevenue: {
      unit: currency,
      name: 'Total Revenues',
      translateId: 'SIGNALS.TOTAL_REV.DISPLAY_NAME'
    },
    consumptionLosses: {
      unit: currency,
      name: 'Energy Consumption',
      translateId: 'SIGNALS.ENERGY_CONSUMP.DISPLAY_NAME'
    },
    marketPrice: {
      unit: `${currency}/MWh`,
      name: 'Avg. Market Price Electricity',
      translateId: 'SIGNALS.AVG_MARKET_ELEC.DISPLAY_NAME'
    },
    PPAElectricityPrice: {
      unit: `${currency}/MWh`,
      name: 'Avg. Electricity Sales Price',
      translateId: 'SIGNALS.AVG_PPA_ELEC.DISPLAY_NAME'
    },
    windSpeed: {
      unit: 'm/s',
      name: 'Average Wind Speed',
      translateId: 'SIGNALS.AVG_WIND_SPEED.DISPLAY_NAME'
    },
    temperature: {
      unit: '°C',
      name: 'Average Ambient Temperature',
      translateId: 'SIGNALS.AVG_TEMP.DISPLAY_NAME'
    },
    theoreticalProduction: {
      unit: 'MWh',
      name: 'Theoretical Production',
      translateId: 'SIGNALS.THEORETICAL_PRODUCTION.DISPLAY_NAME'
    }
  };
}

export function setSeries(data: DSSignalItemData[], currency, translate: TranslateService, type: 'device' | 'signal' = 'signal') {
  const meterSignals = getMeterSignals(currency.getCurrencySymbol());

  const grouped = data.reduce((acc, item) => {
    const deviceName = extractDeviceName(item).replace(/_/g, ' ');
    Object.entries(item).forEach(([key, value]) => {
      if (isValidNumericSignal(key, value)) {
        const itemKey = type === 'signal' ? key : deviceName;
        if (!acc[itemKey]) {
          acc[itemKey] = [];
        }
        acc[itemKey].push(value);
      }
    });
    return acc;
  }, {} as Record<string, number[]>);

  return Object.entries(grouped).map(([signalKey, values]) => {
    const unit = getSignalUnit(signalKey, meterSignals);
    const label = type === 'signal' ? getTranslatedSignalName(signalKey, meterSignals, translate) : signalKey;

    return {
      label,
      data: values,
      unit,
    };
  });
}

export function extractDeviceName(item: DSSignalItemData): string {
  for (const key of deviceKeys) {
    const value = item[key as keyof DSSignalItemData];
    if (value && typeof value === 'string') {
      return value;
    }
  }

  return 'Unknown';
}

export function isValidNumericSignal(key: string, value: unknown): value is number {
  return key !== 'Timestamp' &&
    typeof value === 'number' &&
    !isNaN(value);
}

export function getSignalUnit(signalKey: string, meterSignals: any): string {
  const unitMatch = signalKey.match(/\(([^)]+)\)$/);
  return unitMatch?.[1] || meterSignals[signalKey]?.unit || '-';
}

export function getTranslatedSignalName(signalKey: string, meterSignals: any, translate: TranslateService): string {
  const config = meterSignals[signalKey];
  if (config?.translateId) {
    return translate.instant(config.translateId);
  }
  const cleanKey = signalKey.replace(/\([^)]*\)$/, '').trim();
  return translate.instant(cleanKey || signalKey);
}